import React, { useEffect, useState } from 'react';
import { formatNumber, shortenAddress, formatDateTime } from '../../utils/formatters';
import { ACTIVE_CONTRACTS } from '../../config/contract';
import { Section, InfoRow, StatGrid, Loading } from './AdminUI';
import { fromWei, bpsToPct } from './useAdmin';

const formatDuration = (secs) => {
  if (secs % 86400 === 0) return `${secs / 86400} day(s)`;
  if (secs % 3600 === 0) return `${secs / 3600} hour(s)`;
  if (secs % 60 === 0) return `${secs / 60} min`;
  return `${secs} sec`;
};

const AdminOverview = ({ admin }) => {
  const { read, isOwner } = admin;
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const s = read.storage;
        // Everything in one parallel round
        const [main, tiers] = await Promise.all([
          Promise.all([
            s.totalUsers(), s.getContractBalance(), s.owner(), s.admin(), s.logicContract(),
            s.deploymentTime(), s.TIME_UNIT(), s.baseROI(), s.directReferralPercent(),
            s.binaryMatchPercent(), s.minWithdraw(), s.withdrawDeduction(),
            s.MAX_PROPAGATION_DEPTH(), s.MAX_GENERATION_DEPTH()
          ]),
          Promise.all([0, 1, 2, 3].map(async (i) => {
            const [t, r] = await Promise.all([s.roiThresholds(i), s.roiRates(i)]);
            return { threshold: fromWei(t), rate: r.toNumber() };
          }))
        ]);
        const [
          totalUsers, balance, owner, adminAddr, logic, deployed, timeUnit,
          baseROI, directRef, binary, minW, deduction, propDepth, genDepth
        ] = main;

        setData({
          // Root contract user (1000) is not counted
          totalUsers: Math.max(0, totalUsers.toNumber() - 1),
          balance: fromWei(balance),
          owner, admin: adminAddr, logic,
          deployed: deployed.toNumber(),
          timeUnit: timeUnit.toNumber(),
          baseROI: baseROI.toNumber(),
          directRef: directRef.toNumber(),
          binary: binary.toNumber(),
          minWithdraw: fromWei(minW),
          deduction: deduction.toNumber(),
          propDepth: propDepth.toNumber(),
          genDepth: genDepth.toNumber(),
          tiers
        });
      } catch (err) {
        console.error('Admin overview load error:', err);
      }
    };
    load();
  }, [read]);

  if (!data) return <Loading />;

  const logicMismatch = data.logic.toLowerCase() !== ACTIVE_CONTRACTS.logic.toLowerCase();

  return (
    <>
      <StatGrid items={[
        { label: 'Total Users', value: data.totalUsers },
        { label: 'Contract Balance', value: `${formatNumber(data.balance)} USDT` }
      ]} />

      <Section title="Contracts">
        {isOwner && <InfoRow label="Owner" value={shortenAddress(data.owner, 6)} mono />}
        <InfoRow label="Admin" value={shortenAddress(data.admin, 6)} mono />
        <InfoRow label="Storage" value={shortenAddress(ACTIVE_CONTRACTS.storage, 6)} mono />
        <InfoRow label="Active Logic" value={shortenAddress(data.logic, 6)} mono />
        <InfoRow label="Views" value={shortenAddress(ACTIVE_CONTRACTS.views, 6)} mono />
        <InfoRow label="Deployed" value={formatDateTime(data.deployed)} />
        {isOwner && logicMismatch && (
          <p className="admin-warning">
            Storage points to a different logic contract than this frontend uses. Update contract.js or call setLogicContract.
          </p>
        )}
      </Section>

      <Section title="Income Settings">
        <InfoRow label="Base ROI" value={`${bpsToPct(data.baseROI)}% / period`} />
        {data.tiers.map((t, i) => (
          <InfoRow
            key={i}
            label={`Tier ${i + 1} (≥ ${formatNumber(t.threshold, 0)} direct biz)`}
            value={`${bpsToPct(t.rate)}%`}
          />
        ))}
        <InfoRow label="Direct Referral" value={`${bpsToPct(data.directRef)}%`} />
        <InfoRow label="Binary Match" value={`${bpsToPct(data.binary)}%`} />
      </Section>

      <Section title="System Settings">
        <InfoRow label="Time Unit (ROI / binary period)" value={formatDuration(data.timeUnit)} />
        <InfoRow label="Min Withdraw" value={`${formatNumber(data.minWithdraw)} USDT`} />
        <InfoRow label="Withdraw Deduction" value={`${bpsToPct(data.deduction)}%`} />
        {isOwner && <InfoRow label="Max Propagation Depth" value={data.propDepth} />}
        {isOwner && <InfoRow label="Max Generation Depth" value={data.genDepth} />}
      </Section>
    </>
  );
};

export default AdminOverview;
